import { useEffect, useState } from 'react';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import {
  Callout,
  DateInput,
  ListEmptyState,
  LoadingSpinner,
  Select,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CustomerLookupItem } from '../../types/customer';
import { ShipmentExpenseAnalysisReport } from '../../types/reports';
import { formatMoney } from '../../utils/format';
import { ReportExportButtons } from './ReportExportButtons';
import { exportShipmentExpenseAnalysis } from './exportShipmentReports';

export function ShipmentExpenseAnalysisPage() {
  const labels = t().reports;
  const [customers, setCustomers] = useState<CustomerLookupItem[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [report, setReport] = useState<ShipmentExpenseAnalysisReport | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.getCustomerLookup({ active: 'true' }).then(setCustomers);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getShipmentExpenseAnalysisReport({
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          customerId: customerId ? Number(customerId) : undefined,
        });
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, customerId]);

  function renderTable(
    title: string,
    rows: ShipmentExpenseAnalysisReport['byBillable'],
  ) {
    return (
      <DataTablePanel>
        <h3 className="border-b border-line px-4 py-3 text-sm font-semibold text-ink">
          {title}
        </h3>
        <table className="min-w-full text-left text-sm">
          <thead className={dataTableHeadClass}>
            <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
              <th className="px-4 py-3 font-semibold">{t().common.total}</th>
              <th className="px-4 py-3 font-semibold">{labels.lineCount}</th>
              <th className="px-4 py-3 text-right font-semibold">
                {labels.expenseNet}
              </th>
              <th className="px-4 py-3 text-right font-semibold">
                {labels.expenseVat}
              </th>
              <th className="px-4 py-3 text-right font-semibold">
                {labels.expenseGross}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-b border-line/70">
                <td className="px-4 py-3 font-medium">{row.label}</td>
                <td className="px-4 py-3 tabular-nums">{row.lineCount}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {formatMoney(row.totals.net)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {formatMoney(row.totals.vat)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {formatMoney(row.totals.gross)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataTablePanel>
    );
  }

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <ReportExportButtons
            disabled={!report || loading}
            onExcel={() => {
              if (!report) return;
              exportShipmentExpenseAnalysis({ report, format: 'excel' });
            }}
            onPdf={() => {
              if (!report) return;
              exportShipmentExpenseAnalysis({ report, format: 'pdf' });
            }}
          />
        }
      >
        <FilterField label={labels.dateFrom}>
          <DateInput
            value={dateFrom}
            showClearButton
            showQuickFill={false}
            showFormatHint={false}
            placeholder={labels.dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.dateTo}>
          <DateInput
            value={dateTo}
            showClearButton
            showQuickFill={false}
            showFormatHint={false}
            placeholder={labels.dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </FilterField>
        <FilterField label={t().common.customer}>
          <Select
            value={customerId}
            size="sm"
            showClearButton={Boolean(customerId)}
            placeholder={labels.allCustomers}
            options={customers.map((c) => ({
              value: String(c.id),
              label: c.name,
            }))}
            onChange={(e) => setCustomerId(e.target.value)}
          />
        </FilterField>
      </ListFiltersBar>
      {error ? <Callout variant="danger">{error}</Callout> : null}
      {loading ? (
        <LoadingSpinner message={t().common.loading} />
      ) : !report ? (
        <ListEmptyState title={t().common.empty} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {renderTable(labels.billable, report.byBillable)}
          {renderTable(labels.viewInvoice, report.byType)}
        </div>
      )}
    </PageShell>
  );
}
